import React from 'react'
// libs
import { useSelector, useDispatch } from 'react-redux'
import { change, formValueSelector, isDirty } from 'redux-form'
// hooks
import { useModalData } from 'hooks/index'
// components
import { Modal } from 'components/UI'
// view components
import RecurrenceForm from 'components/Modals/KPIRecurrenceModal/Form'
// constants
import {
  Forms,
  Modals,
  REPEAT_VALUES,
  REPEAT_OPTIONS,
  RECURRENCE_ENDS_VALUES,
  EACH_PERIOD_ENDS_TYPE_OPTIONS
} from 'constants/index'
// utils
import moment from 'moment'
import { trimTz } from 'utils/date'
import { generateKPIRecurrenceRanges } from 'utils/kpi'
import { getOptionByValue, getValueFromOption } from 'utils/form'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'

const PAPER_PROPS = {
  style: {
    margin: 0,
    overflow: 'auto'
  }
}

const formSelector = formValueSelector(Forms.RISK_RECURRENCE_FORM)

const RiskRecurrenceModal: React.FC = () => {
  const dispatch = useDispatch()

  const { risk, updateRisk } = React.useContext(RiskAPIContext)

  const [isLoading, setLoading] = React.useState(false)

  const { days, repeatEveryType, repeatEveryValue, isCurrentFormDirty, eachPeriodEndsType, firstPeriodStartsAt } =
    useSelector((state) => ({
      days: formSelector(state, `days`),
      repeatEveryType: formSelector(state, `repeatEveryType`) || {},
      repeatEveryValue: formSelector(state, `repeatEveryValue`),
      eachPeriodEndsType: formSelector(state, `eachPeriodEndsType`),
      isCurrentFormDirty: isDirty(Forms.RISK_RECURRENCE_FORM)(state),
      firstPeriodStartsAt: formSelector(state, `firstPeriodStartsAt`)
    }))

  const {
    hideModal,
    modalData: { isOpen }
  } = useModalData({ modalType: Modals.RISK_RECURRENCE_MODAL })

  const initialValues = React.useMemo(() => {
    if (risk?.recurrence && Object.keys(risk.recurrence).length > 1) {
      return {
        ...risk.recurrence,
        repeatEveryType: getOptionByValue(risk.recurrence.repeatEveryType, REPEAT_OPTIONS),
        eachPeriodEndsType: getOptionByValue(risk.recurrence.eachPeriodEndsType, EACH_PERIOD_ENDS_TYPE_OPTIONS)
      }
    }

    return { active: true }
  }, [risk])

  const formattedFirstPeriod = firstPeriodStartsAt ? moment(firstPeriodStartsAt).format('L') : null

  // methods
  const handleSubmit = async (data) => {
    try {
      setLoading(true)

      const recurrence = {
        ...data,
        repeatEveryType: getValueFromOption(data.repeatEveryType),
        eachPeriodEndsType: getValueFromOption(data.eachPeriodEndsType),
        recurrenceEndsAt: data.recurrenceEnds === RECURRENCE_ENDS_VALUES.ON ? data.recurrenceEndsAt : null
      }

      await updateRisk({ recurrence })

      hideModal()
    } catch (error) {
      console.log('[RiskRecurrenceModal.handleSubmit]: ', error)
    } finally {
      setLoading(false)
    }
  }

  const changeField = (field, value, form) => dispatch(change(form, field, value))

  const recalculateDates = () => {
    if (isCurrentFormDirty) {
      const startPoint = moment(firstPeriodStartsAt)

      if (!firstPeriodStartsAt) {
        changeField(`firstPeriodStartsAt`, trimTz(moment().startOf('d')), Forms.RISK_RECURRENCE_FORM)

        return
      }

      let shouldCalculate = false

      if (repeatEveryType.value === REPEAT_VALUES.WEEKLY) {
        shouldCalculate = Array.isArray(days) && !!days.length
      }

      if (repeatEveryType.value === REPEAT_VALUES.MONTHLY) {
        shouldCalculate = !!eachPeriodEndsType
      }

      if (shouldCalculate) {
        const { lastDayOfThePeriod } = generateKPIRecurrenceRanges(
          { repeatEveryValue, repeatEveryType, days, eachPeriodEndsType },
          startPoint
        )

        changeField(`firstPeriodEndsAt`, trimTz(lastDayOfThePeriod.startOf('d')), Forms.RISK_RECURRENCE_FORM)
      }
    }
  }

  React.useEffect(recalculateDates, [days, eachPeriodEndsType, formattedFirstPeriod, repeatEveryType, repeatEveryValue])

  return (
    <Modal width='xs' isOpen={isOpen} hideModal={hideModal} disableRestoreFocus PaperProps={PAPER_PROPS}>
      <RecurrenceForm
        isRisk
        hideModal={hideModal}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        form={Forms.RISK_RECURRENCE_FORM}
        repeatEveryType={repeatEveryType.value}
      />
    </Modal>
  )
}

export default RiskRecurrenceModal
